import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, FolderTree, Layers, Pencil, Plus, Trash2, Users } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { useAuthStore } from "@/store/auth";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  canEditScopeDictionary,
  useCategoryHolders,
  useDeleteCategory,
  useDeleteScope,
  useSaveCategory,
  useSaveScope,
  useScopeCategories,
  type CooperationScope,
  type ScopeCategory,
} from "@/features/rfq/api/scopes";

/**
 * Dwupoziomowy słownik prac: kategoria → pozycje.
 *
 * Kategoria jest jednostką uprawnienia — to ją nadajemy firmie. Pozycje pokazują,
 * co konkretnie się pod nią kryje, i służą wyłącznie do opisu. Dlatego kategorie są
 * kartami z własnym nagłówkiem, a nie kolejnymi wierszami płaskiej tabeli.
 */

export function ScopeCategoriesPage() {
  const { user } = useAuthStore();
  const canEdit = canEditScopeDictionary(user);
  usePageTitle("Kategorie i zakresy prac");

  const [showInactive, setShowInactive] = useState(false);
  const { data: categories, isLoading } = useScopeCategories(showInactive);
  const saveCategory = useSaveCategory();
  const deleteCategory = useDeleteCategory();
  const saveScope = useSaveScope();
  const deleteScope = useDeleteScope();

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [holdersOf, setHoldersOf] = useState<ScopeCategory | null>(null);
  const { data: holders } = useCategoryHolders(holdersOf?.id ?? null);
  const [categoryForm, setCategoryForm] = useState<Partial<ScopeCategory> | null>(null);
  const [scopeForm, setScopeForm] = useState<Partial<CooperationScope> | null>(null);
  const [error, setError] = useState("");

  if (isLoading) return <PageLoader />;

  const list = categories ?? [];
  const positionCount = list.reduce((sum, c) => sum + c.scopes.length, 0);

  const submitCategory = () => {
    if (!categoryForm?.name?.trim()) return setError("Nazwa kategorii jest wymagana.");
    setError("");
    saveCategory.mutate(
      {
        id: categoryForm.id,
        name: categoryForm.name.trim(),
        description: categoryForm.description ?? "",
        sort_order: categoryForm.sort_order ?? 0,
        is_active: categoryForm.is_active ?? true,
      },
      {
        onSuccess: () => setCategoryForm(null),
        onError: () => setError("Nie udało się zapisać — sprawdź, czy nazwa nie jest zajęta."),
      },
    );
  };

  const submitScope = () => {
    if (!scopeForm?.name?.trim()) return setError("Nazwa pozycji jest wymagana.");
    setError("");
    saveScope.mutate(
      {
        id: scopeForm.id,
        category: scopeForm.category,
        name: scopeForm.name.trim(),
        description: scopeForm.description ?? "",
        sort_order: scopeForm.sort_order ?? 0,
        is_active: scopeForm.is_active ?? true,
      },
      {
        onSuccess: () => setScopeForm(null),
        onError: () => setError("Nie udało się zapisać — sprawdź, czy nazwa nie jest zajęta."),
      },
    );
  };

  return (
    <div className="max-w-5xl">
      {/* Nagłówek */}
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Kategorie i zakresy prac
          </h1>
          <div className="mt-1 h-0.5 w-8 bg-[var(--color-primary)]" />
          <p className="mt-3 max-w-2xl text-sm text-[var(--color-text-secondary)]">
            Kategorię <strong>nadajemy</strong> firmie — to ona otwiera dostęp do postępowań.
            Pozycje wewnątrz opisują, co konkretnie się pod nią kryje. Obszar działania
            (województwa) ustawia się osobno, na karcie firmy.
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setCategoryForm({ name: "", description: "", sort_order: 0 })}>
            <Plus size={14} className="mr-1.5 inline" /> Nowa kategoria
          </Button>
        )}
      </div>

      {/* Podsumowanie */}
      <div className="mb-5 flex flex-wrap items-center gap-6 border-y border-[var(--color-border)] py-3">
        <span className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <FolderTree size={14} className="text-[var(--color-text-muted)]" />
          Kategorie: <strong className="text-[var(--color-text-primary)]">{list.length}</strong>
        </span>
        <span className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <Layers size={14} className="text-[var(--color-text-muted)]" />
          Pozycje: <strong className="text-[var(--color-text-primary)]">{positionCount}</strong>
        </span>
        <label className="ml-auto flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Pokaż wycofane
        </label>
      </div>

      {/* Kategorie */}
      {list.length === 0 ? (
        <div className="border border-dashed border-[var(--color-border)] px-6 py-12 text-center">
          <FolderTree size={28} className="mx-auto mb-3 text-[var(--color-text-muted)]" />
          <p className="text-sm text-[var(--color-text-secondary)]">
            Nie ma jeszcze żadnej kategorii prac.
          </p>
          {canEdit && (
            <Button
              className="mt-4"
              onClick={() => setCategoryForm({ name: "", description: "", sort_order: 0 })}
            >
              <Plus size={14} className="mr-1.5 inline" /> Dodaj pierwszą
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {list.map((category) => {
            const isOpen = !collapsed[category.id];
            return (
              <section
                key={category.id}
                className="border border-[var(--color-border)] bg-white dark:bg-gray-800"
              >
                {/* Nagłówek kategorii */}
                <header className="flex flex-wrap items-center gap-3 border-l-4 border-[var(--color-primary)] px-4 py-3">
                  <button
                    onClick={() =>
                      setCollapsed((prev) => ({ ...prev, [category.id]: isOpen }))
                    }
                    className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]"
                    aria-label={isOpen ? "Zwiń" : "Rozwiń"}
                  >
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="font-semibold text-[var(--color-text-primary)]">
                        {category.name}
                      </h2>
                      {!category.is_active && <Badge color="gray">Wycofana</Badge>}
                    </div>
                    {category.description && (
                      <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                        {category.description}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => setHoldersOf(category)}
                    className="flex items-center gap-1.5 whitespace-nowrap text-xs text-[var(--color-text-muted)] hover:text-[var(--color-primary)] hover:underline"
                    title="Pokaż firmy z tą kategorią"
                  >
                    <Users size={13} /> {category.contractor_count}
                  </button>
                  <span className="whitespace-nowrap text-xs text-[var(--color-text-muted)]">
                    {category.scopes.length} poz.
                  </span>

                  {canEdit && (
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setCategoryForm(category)}
                        title="Edytuj kategorię"
                        className="p-1.5 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => {
                          const blocked =
                            category.contractor_count > 0 || category.scopes.length > 0;
                          const question = blocked
                            ? `Kategoria „${category.name}” jest w użyciu — zostanie wycofana, a przyznane kwalifikacje i pozycje zostaną zachowane. Kontynuować?`
                            : `Usunąć kategorię „${category.name}”?`;
                          if (confirm(question)) deleteCategory.mutate(category.id);
                        }}
                        title="Usuń kategorię"
                        className="p-1.5 text-[var(--color-text-muted)] hover:text-red-600"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  )}
                </header>

                {/* Pozycje */}
                {isOpen && (
                  <div className="border-t border-[var(--color-border)] px-4 py-3">
                    {category.scopes.length === 0 ? (
                      <p className="text-xs text-[var(--color-text-muted)]">
                        Brak pozycji — kategoria działa, ale nikt nie wie, co dokładnie obejmuje.
                      </p>
                    ) : (
                      <ul className="divide-y divide-[var(--color-border)]">
                        {category.scopes.map((scope) => (
                          <li
                            key={scope.id}
                            className="group flex items-start gap-3 py-2 text-sm"
                          >
                            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[var(--color-text-muted)]" />
                            <div className="min-w-0 flex-1">
                              <span className="text-[var(--color-text-primary)]">
                                {scope.name}
                              </span>
                              {!scope.is_active && (
                                <Badge color="gray" className="ml-2">
                                  Wycofana
                                </Badge>
                              )}
                              {scope.description && (
                                <p className="text-xs text-[var(--color-text-secondary)]">
                                  {scope.description}
                                </p>
                              )}
                            </div>
                            {canEdit && (
                              <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                                <button
                                  onClick={() => setScopeForm(scope)}
                                  title="Edytuj pozycję"
                                  className="p-1 text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                                >
                                  <Pencil size={13} />
                                </button>
                                <button
                                  onClick={() => {
                                    if (confirm(`Usunąć pozycję „${scope.name}”?`))
                                      deleteScope.mutate(scope.id);
                                  }}
                                  title="Usuń pozycję"
                                  className="p-1 text-[var(--color-text-muted)] hover:text-red-600"
                                >
                                  <Trash2 size={13} />
                                </button>
                              </div>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}

                    {canEdit && (
                      <button
                        onClick={() =>
                          setScopeForm({ category: category.id, name: "", sort_order: 0 })
                        }
                        className="mt-2 inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:underline"
                      >
                        <Plus size={12} /> Dodaj pozycję
                      </button>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Kto ma tę kategorię */}
      {holdersOf && (
        <Modal
          open
          onClose={() => setHoldersOf(null)}
          title={`Firmy z kategorią: ${holdersOf.name}`}
          size="lg"
        >
          {!holders ? (
            <p className="text-sm text-[var(--color-text-muted)]">Wczytywanie…</p>
          ) : holders.length === 0 ? (
            <p className="text-sm text-[var(--color-text-muted)]">
              Żadna firma nie ma jeszcze tej kategorii.
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {holders.map((holder) => (
                <li key={holder.contractor_id} className="py-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link
                      to={`/contractors/${holder.contractor_id}`}
                      className="font-medium text-[var(--color-primary)] hover:underline"
                    >
                      {holder.contractor_name}
                    </Link>
                    <Badge color={holder.status === "conditional" ? "yellow" : "green"}>
                      {holder.status_label}
                    </Badge>
                    <span className="text-xs text-[var(--color-text-muted)]">
                      od {new Date(holder.granted_at).toLocaleDateString("pl")}
                      {holder.granted_by_name && ` · ${holder.granted_by_name}`}
                    </span>
                  </div>
                  <p className="mt-0.5 text-xs text-[var(--color-text-secondary)]">
                    {holder.all_positions
                      ? "cała kategoria"
                      : `tylko: ${holder.positions.map((p) => p.name).join(" · ")}`}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </Modal>
      )}

      {/* Formularz kategorii */}
      {categoryForm && (
        <Modal
          open
          onClose={() => setCategoryForm(null)}
          title={categoryForm.id ? "Edytuj kategorię" : "Nowa kategoria"}
        >
          <div className="space-y-4">
            <Field label="Nazwa">
              <input
                autoFocus
                value={categoryForm.name ?? ""}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                className={inputClass}
                placeholder="np. Wykonawstwo sieci nn"
              />
            </Field>
            <Field label="Opis">
              <textarea
                value={categoryForm.description ?? ""}
                onChange={(e) =>
                  setCategoryForm({ ...categoryForm, description: e.target.value })
                }
                rows={3}
                className={inputClass}
              />
            </Field>
            <Field label="Kolejność na liście">
              <input
                type="number"
                value={categoryForm.sort_order ?? 0}
                onChange={(e) =>
                  setCategoryForm({
                    ...categoryForm,
                    sort_order: parseInt(e.target.value, 10) || 0,
                  })
                }
                className={`${inputClass} w-28`}
              />
            </Field>
            {categoryForm.id && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={categoryForm.is_active ?? true}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, is_active: e.target.checked })
                  }
                />
                Aktywna
              </label>
            )}
            <FormFooter
              error={error}
              pending={saveCategory.isPending}
              onCancel={() => setCategoryForm(null)}
              onSubmit={submitCategory}
            />
          </div>
        </Modal>
      )}

      {/* Formularz pozycji */}
      {scopeForm && (
        <Modal
          open
          onClose={() => setScopeForm(null)}
          title={scopeForm.id ? "Edytuj pozycję" : "Nowa pozycja"}
        >
          <div className="space-y-4">
            <Field label="Kategoria">
              <select
                value={scopeForm.category ?? ""}
                onChange={(e) => setScopeForm({ ...scopeForm, category: e.target.value })}
                className={inputClass}
              >
                {list.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Nazwa">
              <input
                autoFocus
                value={scopeForm.name ?? ""}
                onChange={(e) => setScopeForm({ ...scopeForm, name: e.target.value })}
                className={inputClass}
                placeholder="np. Budowa linii kablowych nn"
              />
            </Field>
            <Field label="Opis">
              <textarea
                value={scopeForm.description ?? ""}
                onChange={(e) => setScopeForm({ ...scopeForm, description: e.target.value })}
                rows={2}
                className={inputClass}
              />
            </Field>
            <Field label="Kolejność na liście">
              <input
                type="number"
                value={scopeForm.sort_order ?? 0}
                onChange={(e) =>
                  setScopeForm({ ...scopeForm, sort_order: parseInt(e.target.value, 10) || 0 })
                }
                className={`${inputClass} w-28`}
              />
            </Field>
            {scopeForm.id && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={scopeForm.is_active ?? true}
                  onChange={(e) => setScopeForm({ ...scopeForm, is_active: e.target.checked })}
                />
                Aktywna
              </label>
            )}
            <FormFooter
              error={error}
              pending={saveScope.isPending}
              onCancel={() => setScopeForm(null)}
              onSubmit={submitScope}
            />
          </div>
        </Modal>
      )}
    </div>
  );
}

const inputClass =
  "w-full border border-[var(--color-border)] bg-transparent px-3 py-2 text-sm text-[var(--color-text-primary)]";

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="mb-1 block font-mono text-[11px] uppercase tracking-wide text-[var(--color-text-muted)]">
        {label}
      </label>
      {children}
    </div>
  );
}

function FormFooter({
  error,
  pending,
  onCancel,
  onSubmit,
}: {
  error: string;
  pending: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <>
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="secondary" onClick={onCancel}>
          Anuluj
        </Button>
        <Button onClick={onSubmit} disabled={pending}>
          {pending ? "Zapisywanie…" : "Zapisz"}
        </Button>
      </div>
    </>
  );
}
